// FieldApp.tsx — the house-visit app, rebuilt to the structure officers found clearer.
//
// Three screens, in the order the work happens:
//
//   1. THE ROUND      Address first, name underneath. An officer holding a phone is navigating to
//                     a flat; the address is the primary key in the field and the name is context.
//                     Leading with the name was an office hierarchy applied to a field task.
//
//   2. THE VISIT      Sequential questions in the officer's own language: was anyone home, why is
//                     payment late, which hardship driver, what was agreed, what evidence. The
//                     previous single "What happened?" list conflated three different things —
//                     contact, diagnosis and result — so an ordinary visit could record a result
//                     without ever producing a category.
//
//   3. WHAT HAPPENS   Rendered ENTIRELY from what the server reports it did. The officer is the
//      NEXT           person who has to tell the resident what follows, so they are handed it —
//                     and nothing is added because it would read well. A referral that is recorded
//                     but not transmitted says exactly that.
//
// No admin rail here: this route renders outside the portal shell, with a four-item tab bar.
import { useEffect, useState } from 'react'
import { useAuth } from '../../auth/AuthContext'
import { can } from '../../lib/capabilities'
import { api, ApiError, type AmsVisit } from '../../lib/api'
import { DRIVERS, money } from '../../lib/format'
import AssessmentForm from './AssessmentForm'

type LogResult = Awaited<ReturnType<typeof api.ams.logVisit>>

/** Why payment is late, in the words an officer would use at a door.
 *
 *  "Cannot pay" is NOT in this list for a Property Officer, and its absence is the design. A PO
 *  who sees hardship uses the hand-off outcome below; classifying a household as Category 1
 *  suppresses their letters and fees under a named rule, and that needs the assessment the
 *  Community Liaison Team gathers — household, income, evidence, consent — not an impression at a
 *  door. The server refuses it too (arrears.hardship.assess), so this is not the only guard. */
const WHY_COLLECT: Array<{ cat: string; label: string }> = [
  { cat: '2', label: 'Forgot' },
  { cat: '3', label: 'Refuses' },
  { cat: '4', label: 'Flat empty' },
]
const WHY_HARDSHIP: { cat: string; label: string } = { cat: '1', label: 'Cannot pay' }

const OUTCOMES: Array<{ key: string; label: string }> = [
  { key: 'paid_now', label: 'Paid now' },
  { key: 'payment_plan', label: 'Payment plan' },
  { key: 'promise_to_pay', label: 'Promise to pay' },
  { key: 'refer_agency', label: 'Refer agency' },
  { key: 'none_agreed', label: 'Nothing agreed' },
]

/** The Property Officer's route out of a case that is not theirs. Offered to anyone who cannot
 *  classify hardship, because for them it is the ONLY correct answer to a household in trouble. */
const REFER_CLT = { key: 'refer_clt', label: 'Hardship — pass to the CLT' }

type Tab = 'visits' | 'route' | 'sync' | 'me'

export default function FieldApp() {
  const { me, identity } = useAuth()
  const org = me?.org?.id
  const canAssess = can(identity, 'arrears.hardship.assess')
  const [visits, setVisits] = useState<AmsVisit[] | null>(null)
  const [oversees, setOversees] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState<AmsVisit | null>(null)
  const [done, setDone] = useState<LogResult | null>(null)
  const [tab, setTab] = useState<Tab>('visits')

  const load = () => {
    if (!org) return
    api.ams.visits(org).then(d => { setVisits(d.visits); setOversees(d.oversees) })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.detail : String(e)))
  }
  useEffect(load, [org])

  if (error) return <Frame tab={tab} onTab={setTab}>
    <div className="page-state page-state-error">Could not load: {error}</div></Frame>
  if (!visits) return <Frame tab={tab} onTab={setTab}>
    <div className="page-state">Loading…</div></Frame>

  if (done) {
    return (
      <Frame tab={tab} onTab={setTab}>
        <WhatHappensNext
          result={done}
          onNext={id => {
            const v = visits.find(x => x.id === id)
            setDone(null); setOpen(v ?? null); if (!v) load()
          }}
          onBack={() => { setDone(null); setOpen(null); load() }} />
      </Frame>
    )
  }

  if (open) {
    // The CLT gets a different form, not a different button on the same one. A hardship visit and
    // a collection visit ask different questions for different reasons — the CLT is there to
    // understand a household, the Property Officer to confirm a payment — and the shared form they
    // used to have made the CLT's job look like collection with extra fields.
    const assessing = canAssess && open.category === '1'
    return (
      <Frame tab={tab} onTab={setTab}>
        {assessing ? (
          <AssessmentForm
            org={org!} caseId={open.caseId} visitId={open.id} resident={open.residentName}
            onDone={r => setDone({
              visitId: open.id, resident: r.resident, address: open.address,
              effects: r.effects, next: null,
            })}
            onBack={() => setOpen(null)} />
        ) : (
          <VisitForm visit={open} org={org!} onDone={setDone} onBack={() => setOpen(null)} />
        )}
      </Frame>
    )
  }

  const todo = visits.filter(v => v.status === 'scheduled')
  const logged = visits.filter(v => v.status !== 'scheduled')

  if (tab === 'route') return <Frame tab={tab} onTab={setTab}><Route visits={todo} /></Frame>
  if (tab === 'sync') return <Frame tab={tab} onTab={setTab}><Sync todo={todo.length} /></Frame>
  if (tab === 'me') return <Frame tab={tab} onTab={setTab}>
    <Me name={me?.user.name} role={me?.designation ?? me?.role} oversees={oversees} /></Frame>

  return (
    <Frame tab={tab} onTab={setTab}>
      <div className="fld-head">
        <small>{greeting()}{me?.user.name ? `, ${me.user.name.split(' ')[0]}` : ''}</small>
        <b>{oversees ? `The round · ${todo.length}` : `Today's visits · ${todo.length}`}</b>
      </div>
      <div className="fld-body">
        {oversees && (
          <p className="fld-tip">
            You are seeing the whole round because you oversee it. Officers see only their own.
          </p>
        )}
        <p className="fld-tip">
          Residents in Category 1 are visited to understand, not to collect. Start by asking what
          has changed.
        </p>

        {/* One column on a phone, as many as fit on a desktop. The round is a list of equals —
            there is no order that makes card three more important than card nine — so the extra
            width goes into showing more of it at once rather than into a wider card nobody asked
            for. A planner reviewing a round of nineteen should see the round, not scroll it. */}
        <div className="fld-grid">
          {todo.map(v => <VisitCard key={v.id} v={v} onOpen={() => setOpen(v)} />)}
        </div>

        {logged.length > 0 && <h3 className="fld-sub">Logged · {logged.length}</h3>}
        <div className="fld-grid">
          {logged.slice(0, 20).map(v => (
            <div key={v.id} className="fld-card done">
              <div className="fld-card-top">
                <b>{shortAddress(v.address)}</b>
                <span className="muted xs">{v.outcome?.replace(/_/g, ' ') ?? 'logged'}</span>
              </div>
              <div className="muted sm">{v.residentName}</div>
            </div>
          ))}
        </div>

        {todo.length === 0 && logged.length === 0 && (
          <p className="muted">No visits assigned. This is a real result, not a failed load.</p>
        )}
      </div>
    </Frame>
  )
}

/** Address first. A round is walked, not read. */
function VisitCard({ v, onOpen }: { v: AmsVisit; onOpen: () => void }) {
  return (
    <button className="fld-card" onClick={onOpen}>
      <div className="fld-card-top">
        <b>{shortAddress(v.address)}</b>
        <span className={`tag c${v.category ?? 'u'}`}>
          {v.category ? `${v.category}${v.hardshipCode ? ` · ${v.hardshipCode}` : ''}` : 'New'}
        </span>
      </div>
      <div className="muted sm">
        {v.residentName} · {money(v.balanceCents)} · {v.daysOverdue} days
      </div>
      {/* WHY THIS DOOR. `nextAction` is what the system decided should happen next — a broken
          promise, a demand recommended, a hand-off from another team. That is a reason; "Follow
          up" is a category restated as a sentence and tells an officer nothing they did not
          already see on the tag beside it. */}
      <div className="muted xs">
        <b>Why:</b>{' '}
        {v.nextAction
          ?? (v.category === '1' ? 'Understand circumstances'
              : v.category ? 'Follow up' : 'First visit — assess category')}
      </div>
    </button>
  )
}

function VisitForm({ visit, org, onDone, onBack }:
  { visit: AmsVisit; org: string; onDone: (r: LogResult) => void; onBack: () => void }) {
  const { identity } = useAuth()
  // Who may classify a household as Category 1. The Community Liaison Team and an Arrears Manager
  // do; a Property Officer does not, and for them the form offers the hand-off instead.
  const mayAssess = can(identity, 'arrears.hardship.assess')
  const [atHome, setAtHome] = useState<boolean | null>(null)
  const [cat, setCat] = useState(visit.category ?? '')
  const [driver, setDriver] = useState(visit.hardshipCode ?? '')
  const [outcome, setOutcome] = useState('')
  const [planAmt, setPlanAmt] = useState('')
  const [planStart, setPlanStart] = useState('')
  const [promiseOn, setPromiseOn] = useState('')
  const [promiseAmt, setPromiseAmt] = useState('')
  const [agency, setAgency] = useState('SSO')
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  // Captured on the device when the officer records it, which may be well before it syncs. Both
  // times are kept: this is what happened, the server time is when we came to know it.
  const [capturedAt] = useState(() => new Date().toISOString())

  async function save() {
    setBusy(true); setErr(null)
    try {
      onDone(await api.ams.logVisit(org, visit.id, {
        atHome: atHome ?? false,
        // A hand-off records no category. That is the whole point of it.
        category: atHome && outcome !== 'refer_clt' ? (cat || null) : null,
        hardshipCode: atHome && cat === '1' ? driver : null,
        outcome: atHome ? outcome : 'no_contact',
        planAmountCents: outcome === 'payment_plan' && planAmt
          ? Math.round(parseFloat(planAmt) * 100) : null,
        planStartOn: outcome === 'payment_plan' ? planStart || null : null,
        promiseDate: outcome === 'promise_to_pay' ? promiseOn || null : null,
        promiseAmountCents: outcome === 'promise_to_pay' && promiseAmt
          ? Math.round(parseFloat(promiseAmt) * 100) : null,
        referAgency: outcome === 'refer_agency' ? agency : null,
        notes: notes || null,
        capturedAt,
      }))
    } catch (e) {
      setErr(e instanceof ApiError ? e.detail : String(e))
    } finally { setBusy(false) }
  }

  const ready = atHome === false
    // The hand-off needs a note and nothing else: the officer is not diagnosing, so requiring a
    // category before they may pass the case on would force exactly the guess this route removes.
    || (atHome === true && outcome === 'refer_clt' && notes.trim().length > 0)
    || (atHome === true && !!cat && (cat !== '1' || !!driver) && !!outcome
        && (outcome !== 'payment_plan' || (!!planAmt && !!planStart))
        && (outcome !== 'promise_to_pay' || !!promiseOn))

  return (
    // On a phone this is one column, exactly as it was at a door. On a desktop the resident's
    // details become a panel BESIDE the questions instead of a banner above them: the questions
    // are sequential and the context is needed throughout, so scrolling it off the top is the one
    // thing the layout must not do. The question column keeps a readable measure — a form field
    // stretched to 1100px is harder to fill in than one at 560px, not easier.
    <div className="fld-work">
      <div className="fld-head fld-context">
        <button className="fld-back" onClick={onBack}>‹ Visits</button>
        <b>{shortAddress(visit.address)}</b>
        <small>{visit.residentName} · {money(visit.balanceCents)} · {visit.daysOverdue} days</small>
      </div>
      <div className="fld-body fld-form">
        <Q label="Was anyone home?">
          <Chip on={atHome === true} onClick={() => setAtHome(true)}>Yes</Chip>
          <Chip on={atHome === false} onClick={() => setAtHome(false)}>No — leave card</Chip>
        </Q>

        {atHome === false && (
          <p className="fld-note">
            Recorded as no contact. Nothing escalates because of this visit — not answering is not
            evidence that someone can pay.
          </p>
        )}

        {atHome === true && (
          <>
            <Q label="Why is payment late?">
              {WHY_COLLECT.map(w => (
                <Chip key={w.cat} on={cat === w.cat} onClick={() => setCat(w.cat)}>
                  {w.label} <span className="xs">Cat {w.cat}</span>
                </Chip>
              ))}
              {/* Only for an officer who may actually record it. For everyone else the hand-off
                  outcome below is the answer, and offering a chip that the server will refuse
                  would be a trap rather than a feature. */}
              {mayAssess && (
                <Chip on={cat === WHY_HARDSHIP.cat}
                      onClick={() => setCat(WHY_HARDSHIP.cat)}>
                  {WHY_HARDSHIP.label} <span className="xs">Cat 1</span>
                </Chip>
              )}
            </Q>

            {cat === '1' && (
              <Q label="Hardship reason">
                {Object.entries(DRIVERS).map(([k, v]) => (
                  <Chip key={k} on={driver === k} onClick={() => setDriver(k)}>{k} {v}</Chip>
                ))}
              </Q>
            )}

            <Q label="What was agreed?">
              {(mayAssess ? OUTCOMES : [...OUTCOMES, REFER_CLT]).map(o => (
                <Chip key={o.key} on={outcome === o.key} onClick={() => setOutcome(o.key)}>
                  {o.label}
                </Chip>
              ))}
              {outcome === 'payment_plan' && (
                <div className="fld-inline">
                  <input inputMode="decimal" placeholder="Amount / month"
                         value={planAmt} onChange={e => setPlanAmt(e.target.value)} />
                  <input type="date" value={planStart}
                         onChange={e => setPlanStart(e.target.value)} />
                </div>
              )}
              {outcome === 'promise_to_pay' && (
                <div className="fld-inline">
                  <input inputMode="decimal" placeholder="Amount"
                         value={promiseAmt} onChange={e => setPromiseAmt(e.target.value)} />
                  <input type="date" value={promiseOn}
                         onChange={e => setPromiseOn(e.target.value)} />
                </div>
              )}
              {outcome === 'refer_clt' && (
                <p className="fld-note">
                  The Community Liaison Team will visit and decide the category — you do not.
                  Saving this pauses fee-bearing and legal stages on the account straight away,
                  before anyone has assessed. <b>Say what you saw in the notes</b>: they are going
                  to that door on the strength of it.
                </p>
              )}
              {outcome === 'refer_agency' && (
                <>
                  <div className="fld-inline">
                    <select value={agency} onChange={e => setAgency(e.target.value)}>
                      {['SSO', 'SSA', 'ComCare', 'MediFund', 'Employment support', 'VWO']
                        .map(a => <option key={a}>{a}</option>)}
                    </select>
                  </div>
                  <p className="fld-note">
                    The referral is <b>recorded</b> on this case. The system does not send it — you
                    still have to make it.
                  </p>
                </>
              )}
            </Q>
          </>
        )}

        <Q label="Notes">
          <textarea rows={3} value={notes} onChange={e => setNotes(e.target.value)}
                    placeholder="What the resident told you" />
        </Q>

        {/* The shape is here because it is part of the visit; the capture is not built, and a
            button that silently does nothing would be worse than one that says so. */}
        <Q label="Evidence">
          <span className="chip-off">Photo</span>
          <span className="chip-off">Upload proof</span>
          <span className="chip-off">Resident signs</span>
          <p className="fld-note">Capture is not built yet. Record what you saw in the notes.</p>
        </Q>

        {err && <p className="fld-note err">Not saved: {err}</p>}
        <button className="fld-save" disabled={!ready || busy} onClick={save}>
          {busy ? 'Saving…' : 'Save visit'}
        </button>
      </div>
    </div>
  )
}

/** Every line comes from the server's `effects`. Nothing is invented for the sake of the screen. */
function WhatHappensNext({ result, onNext, onBack }:
  { result: LogResult; onNext: (visitId: string) => void; onBack: () => void }) {
  return (
    <div className="fld-work">
      <div className="fld-head fld-context">
        <small>Saved</small>
        <b>Visit recorded ✓</b>
        <small>{shortAddress(result.address)} · {result.resident}</small>
      </div>
      <div className="fld-body fld-form">
        <div className="fld-done-grid">
        <div className="fld-card static ok">
          <h4>What happens next</h4>
          {result.effects.length === 0 ? (
            <p className="muted sm">
              The visit is on the record. Nothing else changed — no category was set and nothing
              was agreed.
            </p>
          ) : (
            <ul className="fld-effects">
              {result.effects.map((e, i) => (
                <li key={i}>
                  <b>{e.summary}</b>
                  {e.detail && <div className="muted sm">{e.detail}</div>}
                </li>
              ))}
            </ul>
          )}
        </div>

        {result.next ? (
          <div className="fld-card static">
            <h4>Next on the round</h4>
            <div><b>{shortAddress(result.next.address)}</b></div>
            <div className="muted sm">{result.next.residentName}</div>
            <button className="fld-save" style={{ marginTop: 10 }}
                    onClick={() => onNext(result.next!.visitId)}>
              Start next visit
            </button>
          </div>
        ) : (
          <div className="fld-card static">
            <h4>That is the round</h4>
            <p className="muted sm">Nothing else is scheduled for you.</p>
          </div>
        )}
        </div>
        <button className="fld-ghost" onClick={onBack}>Back to visits</button>
      </div>
    </div>
  )
}

/** Block order, not distance. We do not geocode, so the app does not claim metres it cannot know. */
function Route({ visits }: { visits: AmsVisit[] }) {
  const byBlock = new Map<string, AmsVisit[]>()
  for (const v of [...visits].sort((a, b) => a.address.localeCompare(b.address))) {
    const b = v.address.split(' #')[0] ?? v.address
    byBlock.set(b, [...(byBlock.get(b) ?? []), v])
  }
  return (
    <>
      <div className="fld-head"><b>Route · {visits.length} visits</b>
        <small>Grouped by block, in order. Distances need geocoding and are not shown.</small></div>
      <div className="fld-body">
        {[...byBlock.entries()].map(([block, vs], i) => (
          <div key={block} className="fld-card static">
            <div className="fld-card-top"><b>{i + 1}. {block}</b>
              <span className="muted sm">{vs.length} visit{vs.length > 1 ? 's' : ''}</span></div>
            {vs.map(v => (
              <div key={v.id} className="muted sm">
                #{v.address.split('#')[1]} · {v.residentName}
              </div>
            ))}
          </div>
        ))}
        {visits.length === 0 && <p className="muted">Nothing scheduled.</p>}
      </div>
    </>
  )
}

/** Sync is a visible state because offline is the field's normal condition — even before we
 *  support it. Saying so is better than a tab that implies a queue we do not have. */
function Sync({ todo }: { todo: number }) {
  return (
    <>
      <div className="fld-head"><b>Sync</b><small>Everything saves straight to the server</small></div>
      <div className="fld-body">
        <div className="fld-card static ok">
          <h4>Nothing waiting</h4>
          <p className="muted sm">
            Visits save immediately, so there is no queue on this device.
          </p>
        </div>
        <div className="fld-card static">
          <h4>Offline is not supported yet</h4>
          <p className="muted sm">
            This app needs a connection to load the round and to save a visit. In a lift lobby with
            no signal it will not work. A local queue — record now, sync when signal returns — is
            the next piece of work.
          </p>
        </div>
        <p className="muted sm">{todo} visit{todo === 1 ? '' : 's'} still to do.</p>
      </div>
    </>
  )
}

function Me({ name, role, oversees }:
  { name?: string; role?: string | null; oversees: boolean }) {
  return (
    <>
      <div className="fld-head"><b>{name ?? 'Signed in'}</b><small>{role}</small></div>
      <div className="fld-body">
        <div className="fld-card static">
          <div className="fld-row"><span className="muted">Sees</span>
            <b>{oversees ? 'The whole round' : 'Visits assigned to me'}</b></div>
        </div>
        <div className="fld-card static">
          <h4>Data on this device</h4>
          <p className="muted sm">
            Nothing is stored here — every screen reads from the server. Once offline capture
            exists, visit notes and photographs will live on the phone between capture and sync,
            and that needs encryption at rest and a remote wipe path before real residents are
            involved.
          </p>
        </div>
      </div>
    </>
  )
}

// ── Small pieces ──────────────────────────────────────────────────────────────────────────────
/**
 * The page frame: four sub-views and the switch between them.
 *
 * It carries NO navigation chrome of its own. This page lives inside the portal shell like every
 * other arrears screen, so the rail (desktop) or the menu button (phone) is already there — and it
 * is the same rail and the same button as on Overview or Approvals. The earlier version supplied
 * its own bar, which made House visits the one page where the navigation changed shape.
 *
 * The tab strip is the exception, and it is not navigation between pages — it is a switch between
 * four views of one page. It renders where each environment expects it: pinned to the bottom edge
 * on a phone, where a thumb is, and as an ordinary tab strip under the heading on a desktop, where
 * a bottom-pinned bar on a 1440px screen would be an affectation.
 */
function Frame({ tab, onTab, children }:
  { tab: Tab; onTab: (t: Tab) => void; children: React.ReactNode }) {
  const tabs = (
    <nav className="fld-tabs" role="tablist" aria-label="House visit views">
      {(['visits', 'route', 'sync', 'me'] as Tab[]).map(t => (
        <button key={t} role="tab" aria-selected={tab === t}
                className={tab === t ? 'on' : ''} onClick={() => onTab(t)}>
          {t === 'visits' ? 'Visits' : t === 'route' ? 'Route' : t === 'sync' ? 'Sync' : 'Me'}
        </button>
      ))}
    </nav>
  )
  return (
    <div className="fld">
      {/* The page title every other arrears screen has. Hidden on a phone, where the shell's top
          bar already names where you are and 844px of height is not spent on saying it twice. */}
      <h2 className="fld-title">House visits</h2>
      {tabs}
      <div className="fld-screen">{children}</div>
    </div>
  )
}

function Q({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="fld-card static">
      <h4>{label}</h4>
      <div className="fld-chips">{children}</div>
    </div>
  )
}

function Chip({ on, onClick, children }:
  { on: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button className={`fld-chip${on ? ' on' : ''}`} onClick={onClick}>{children}</button>
  )
}

/** "Blk 802 Yishun Ring Rd #08-114" → "Blk 802 #08-114". The street is noise once you are there. */
function shortAddress(a: string): string {
  const [blk = a, unit] = a.split(' #')
  const b = blk.match(/^Blk\s+\S+/)?.[0] ?? blk
  return unit ? `${b} #${unit}` : b
}

function greeting(): string {
  const h = new Date().getHours()
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening'
}
